import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from 'prop-types';
import profaLogo from "../../src/assets/logo-3.webp";

function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const location = useLocation();
  const mobileMenuRef = useRef(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const scrolled = currentScrollPos > 10;
      
      if (scrolled !== isScrolled) setIsScrolled(scrolled);
      setScrollPosition(currentScrollPos);
    };

    // Add passive listener for better mobile performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Calculate blur intensity based on scroll position
  const getBlurIntensity = () => {
    // Start with minimum blur and increase with scroll
    const minBlur = 5;
    const maxBlur = 15;
    const scrollThreshold = 200;
    
    // Calculate blur value between min and max based on scroll position
    const blurValue = Math.min(
      maxBlur,
      minBlur + (scrollPosition / scrollThreshold) * (maxBlur - minBlur)
    );
    
    return Math.floor(blurValue);
  };

  // Calculate background opacity based on scroll position
  const getBackgroundOpacity = () => {
    const minOpacity = 40;
    const maxOpacity = 90;
    const scrollThreshold = 300;
    
    const opacityValue = Math.min(
      maxOpacity,
      minOpacity + (scrollPosition / scrollThreshold) * (maxOpacity - minOpacity)
    );
    
    return opacityValue / 100;
  };

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300`}
      style={{
        backgroundColor: isScrolled ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
        backdropFilter: isScrolled ? 'blur(3px)' : 'none',
        WebkitBackdropFilter: isScrolled ? 'blur(3px)' : 'none',
        boxShadow: isScrolled ? '0 4px 30px rgba(0, 0, 0, 0.1)' : 'none',
        borderBottom: isScrolled ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'
      }}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center"
              onClick={() => setIsOpen(false)}
            >
              <img 
                src={profaLogo} 
                alt="Profa Logo"
                width="62"
                height="62" 
                className="h-[3.85rem] w-auto object-contain transform hover:scale-110 transition-transform duration-200 brightness-125 contrast-125"
                loading="eager"
                decoding="async"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-8">
            <NavLink to="/o-meni/">O MENI</NavLink>
            <NavLink to="/astroloske-konsultacije/">ASTROLOŠKE KONSULTACIJE</NavLink>
            <NavLink to="/hipnoze/" isButton>HIPNOZE</NavLink>
            <NavLink to="/kontakt/">KONTAKT</NavLink>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-1.5 text-white hover:text-white focus:outline-none touch-manipulation"
              aria-expanded={isOpen}
              aria-label="Toggle menu"
            >
              <span className="sr-only">Open main menu</span>
              <div className="relative w-4 h-3">
                <span 
                  className={`absolute w-4 h-0.5 bg-current transform transition-all duration-200 ease-out ${
                    isOpen ? 'rotate-45 translate-y-1' : '-translate-y-0.5'
                  }`} 
                />
                <span 
                  className={`absolute w-4 h-0.5 bg-current transform transition-all duration-200 ease-out translate-y-1 ${
                    isOpen ? 'opacity-0' : 'opacity-100'
                  }`} 
                />
                <span 
                  className={`absolute w-4 h-0.5 bg-current transform transition-all duration-200 ease-out ${
                    isOpen ? '-rotate-45 translate-y-1' : 'translate-y-2.5'
                  }`} 
                />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div 
          ref={mobileMenuRef}
          className={`md:hidden fixed inset-x-3 top-20 transition-all duration-300 ease-in-out transform rounded-3xl border border-white/15 shadow-[0_8px_32px_rgba(0,0,0,0.5)] mobile-menu mobile-menu-blur mobile-menu-glass ${
            isOpen 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 -translate-y-4 pointer-events-none'
          }`}
          style={{
            backgroundColor: `rgba(30, 27, 46, ${getBackgroundOpacity()})`,
            backdropFilter: `blur(${getBlurIntensity()}px)`,
            WebkitBackdropFilter: `blur(${getBlurIntensity()}px)`
          }}
        >
          {/* Glass reflection effect */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 via-transparent to-blue-500/5" />
          </div>

          <div className="relative flex flex-col p-5 space-y-3 mobile-menu-content">
            {/* Subtle divider between items */}
            <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            
            <MobileNavLink to="/o-meni/">O MENI</MobileNavLink>
            <MobileNavLink to="/astroloske-konsultacije/">ASTROLOŠKE KONSULTACIJE</MobileNavLink>
            <MobileNavLink to="/hipnoze/">HIPNOZE</MobileNavLink>
            <MobileNavLink to="/kontakt/">KONTAKT</MobileNavLink>

            {/* Bottom highlight */}
            <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-purple-500/5 to-transparent rounded-b-3xl pointer-events-none" />
          </div>
        </div>
      </nav>
    </header>
  );
}

// Optimized NavLink components
function NavLink({ to, children, isButton = false }) {
  if (isButton) {
    return (
      <Link
        to={to}
        className="px-4 py-2 bg-[#8B5CF6] text-white font-bold tracking-wide rounded-md hover:bg-[#2196F3] transition-colors duration-200 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
      >
        {children}
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className="relative group text-white text-lg font-[Arial] font-normal tracking-wide transition-colors duration-200"
    >
      <span className="relative">
        {children}
        <span className="absolute left-1/2 transform -translate-x-1/2 -bottom-2 w-2/3 h-[2px] bg-gradient-to-r from-[#8B5CF6] via-[#2196F3] to-[#8B5CF6] transition-all duration-300 ease-in-out opacity-0 group-hover:opacity-80 rounded-full shadow-sm" />
      </span>
    </Link>
  );
}

NavLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isButton: PropTypes.bool
};

const MobileNavLink = ({ to, children }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link
      to={to}
      className="group relative block mobile-menu-item text-white"
    >
      <div className={`relative p-3.5 rounded-xl transition-all duration-300 ease-out
        ${isActive 
          ? 'bg-[#1E1B2E] shadow-[inset_0_1px_2px_rgba(139,92,246,0.3)]'
          : 'hover:bg-[#1E1B2E] hover:shadow-[inset_0_1px_2px_rgba(255,255,255,0.1)] max-md:bg-[#1E1B2E]/50 max-md:hover:bg-[#1E1B2E]/80'
        }`}
      >
        {/* Highlight effect - Enhanced for mobile */}
        <div className={`absolute -inset-[1px] rounded-xl transition-all duration-300 ease-out
          bg-gradient-to-br from-purple-500/30 via-transparent to-blue-500/30 
          ${isActive 
            ? 'opacity-100 scale-100' 
            : 'max-md:opacity-30 max-md:group-hover:opacity-90 max-md:group-active:opacity-100 max-md:group-active:scale-[0.98] md:opacity-0 md:group-hover:opacity-50'
          }`}
        />
        
        {/* Content wrapper */}
        <div className="relative flex items-center justify-between">
          {/* Text with gradient on active - Enhanced for mobile */}
          <div className={`transform transition-all duration-300 ${
            isActive ? 'translate-x-2' : 'group-hover:translate-x-2'
          }`}>
            <span 
              className={`text-lg font-medium ${
                isActive 
                  ? 'bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent'
                  : 'max-md:font-semibold max-md:drop-shadow-[0_0_6px_rgba(255,255,255,0.4)] md:text-white/90 md:group-hover:text-white md:group-hover:drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]'
              }`}
              style={{ color: isActive ? 'transparent' : 'white' }}
            >
              {children}
            </span>
          </div>
          
          {/* Animated indicator - Enhanced for mobile */}
          <div className={`arrow-container flex items-center transition-all duration-300 ${
            isActive 
              ? 'opacity-100 translate-x-0'
              : 'max-md:opacity-40 max-md:-translate-x-2 max-md:group-hover:opacity-100 max-md:group-hover:translate-x-0 md:opacity-0 md:-translate-x-4 md:group-hover:opacity-100 md:group-hover:translate-x-0'
          }`}>
            <div className="relative h-6 w-6">
              <span className={`absolute inset-0 rounded-full transition-all duration-300 ${
                isActive 
                  ? 'bg-gradient-to-r from-purple-400 to-blue-400 blur-[8px] opacity-70'
                  : 'max-md:bg-white/50 max-md:blur-[6px] max-md:opacity-20 max-md:group-hover:opacity-60 md:bg-white/50 md:blur-[6px] md:opacity-0 md:group-hover:opacity-40'
              }`} />
              <svg 
                className={`relative h-6 w-6 transition-colors duration-300 ${
                  isActive ? 'text-purple-400 drop-shadow-[0_0_8px_rgba(139,92,246,0.5)]' : 'text-white'
                }`}
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Border effect - Enhanced for mobile */}
        <div 
          className={`absolute inset-0 rounded-xl transition-all duration-300 
            ${isActive 
              ? 'ring-1 ring-purple-500/30 shadow-[0_0_15px_rgba(139,92,246,0.3)]' 
              : 'ring-1 max-md:ring-white/20 max-md:group-hover:ring-white/40 md:ring-white/5 md:group-hover:ring-white/10 max-md:shadow-[0_0_8px_rgba(255,255,255,0.15)]'
            }
          `}
          data-component-name="MobileNavLink"
        />
      </div>
    </Link>
  );
};

MobileNavLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};

export default Header;
